import { useMemo } from 'react';
import './WorkflowVisualization.css';

export type PipelineStep = {
  id: string;
  label: string;
  description: string;
};

export type PipelineStepStatus = 'not_started' | 'in_progress' | 'complete';

export type PipelineStepRecord = {
  stepId: string;
  status: PipelineStepStatus;
  note?: string | null;
  updatedAt: number | null;
};

export type WorkflowActor = 'developer' | 'system' | 'admin' | 'reviewer';

interface WorkflowVisualizationProps {
  steps: PipelineStep[];
  currentSteps: PipelineStepRecord[];
  reviewStatus?: 'pending' | 'approved' | 'rejected' | 'changes_requested' | null;
  reviewNote?: string | null;
  reviewer?: string | null;
  compact?: boolean;
  showTimeline?: boolean;
}

const STEP_ACTORS: Record<string, WorkflowActor> = {
  manifest: 'developer',
  build: 'developer',
  sandbox_upload: 'system',
  qa_check: 'system',
  store_publish: 'admin',
};

const ACTOR_LABELS: Record<WorkflowActor, string> = {
  developer: 'Developer',
  system: 'System',
  admin: 'Admin',
  reviewer: 'Reviewer',
};

const ACTOR_COLORS: Record<WorkflowActor, string> = {
  developer: '#3b82f6',
  system: '#8b5cf6',
  admin: '#f59e0b',
  reviewer: '#10b981',
};

const STATUS_ICONS: Record<PipelineStepStatus, string> = {
  not_started: '○',
  in_progress: '◐',
  complete: '●',
};

function formatTimestamp(timestamp: number | null): string {
  if (!timestamp) return 'Not started';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

export default function WorkflowVisualization({
  steps,
  currentSteps,
  reviewStatus,
  reviewNote,
  reviewer,
  compact = false,
  showTimeline = true,
}: WorkflowVisualizationProps) {
  const stepsMap = useMemo(() => {
    const map = new Map<string, PipelineStepRecord>();
    currentSteps.forEach(step => map.set(step.stepId, step));
    return map;
  }, [currentSteps]);

  const currentStepIndex = useMemo(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      const record = stepsMap.get(steps[i].id);
      if (record?.status === 'complete') {
        return i < steps.length - 1 ? i + 1 : i;
      }
      if (record?.status === 'in_progress') {
        return i;
      }
    }
    return 0;
  }, [steps, stepsMap]);

  if (compact) {
    return (
      <div className="workflow-compact">
        {steps.map((step, index) => {
          const record = stepsMap.get(step.id);
          const status = record?.status || 'not_started';

          return (
            <div
              key={step.id}
              className={`workflow-dot workflow-dot--${status}`}
              title={`${step.label}: ${status.replace('_', ' ')}`}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="workflow-visualization">
      {/* Flow Chart */}
      <div className="workflow-flowchart">
        {steps.map((step, index) => {
          const record = stepsMap.get(step.id);
          const status = record?.status || 'not_started';
          const actor = STEP_ACTORS[step.id] || 'system';
          const isActive = index === currentStepIndex;
          const isComplete = status === 'complete';
          const isInProgress = status === 'in_progress';

          return (
            <div key={step.id} className="workflow-step-container">
              <div
                className={`workflow-step ${isActive ? 'workflow-step--active' : ''} ${
                  isComplete ? 'workflow-step--complete' : ''
                } ${isInProgress ? 'workflow-step--in-progress' : ''}`}
              >
                <div className="workflow-step-header">
                  <div className="workflow-step-icon" style={{ color: ACTOR_COLORS[actor] }}>
                    {STATUS_ICONS[status]}
                  </div>
                  <div className="workflow-step-info">
                    <div className="workflow-step-title">{step.label}</div>
                    <div className="workflow-step-meta">
                      <span className="workflow-step-actor" style={{ color: ACTOR_COLORS[actor] }}>
                        {ACTOR_LABELS[actor]}
                      </span>
                      {record?.updatedAt && (
                        <>
                          <span className="workflow-step-separator">•</span>
                          <span className="workflow-step-time">{formatTimestamp(record.updatedAt)}</span>
                        </>
                      )}
                    </div>
                    {!compact && <div className="workflow-step-description">{step.description}</div>}
                    {record?.note && (
                      <div className="workflow-step-note">
                        <span className="workflow-step-note-label">Note:</span> {record.note}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div className={`workflow-step-badge workflow-step-badge--${status}`}>
                  {status.replace('_', ' ')}
                </div>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div
                  className={`workflow-connector ${
                    isComplete ? 'workflow-connector--complete' : ''
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Review Status */}
      {reviewStatus && (
        <div className={`workflow-review workflow-review--${reviewStatus}`}>
          <div className="workflow-review-header">
            <div className="workflow-review-icon">
              {reviewStatus === 'approved' && '✓'}
              {reviewStatus === 'rejected' && '✗'}
              {reviewStatus === 'pending' && '⏳'}
              {reviewStatus === 'changes_requested' && '⚠'}
            </div>
            <div className="workflow-review-info">
              <div className="workflow-review-title">
                Review Status: <strong>{reviewStatus.replace('_', ' ')}</strong>
              </div>
              {reviewer && (
                <div className="workflow-review-meta">
                  Reviewed by: <span className="workflow-review-reviewer">{reviewer}</span>
                </div>
              )}
            </div>
          </div>
          {reviewNote && (
            <div className="workflow-review-note">
              <strong>Feedback:</strong> {reviewNote}
            </div>
          )}
        </div>
      )}

      {/* Timeline View */}
      {showTimeline && (
        <div className="workflow-timeline">
          <h4 className="workflow-timeline-title">Activity Timeline</h4>
          <div className="workflow-timeline-list">
            {currentSteps.length === 0 ? (
              <div className="workflow-timeline-empty">
                <p className="ui-muted-text">No pipeline activity yet</p>
                <p className="ui-muted-text" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Pipeline steps will appear here as your plugin progresses through the workflow.
                </p>
              </div>
            ) : (
              <>
                {currentSteps
                  .filter(step => step.updatedAt || step.status !== 'not_started')
                  .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
                  .map(step => {
                    const stepDef = steps.find(s => s.id === step.stepId);
                    const actor = STEP_ACTORS[step.stepId] || 'system';

                    return (
                      <div key={step.stepId} className="workflow-timeline-item">
                        <div
                          className="workflow-timeline-dot"
                          style={{ backgroundColor: ACTOR_COLORS[actor] }}
                        />
                        <div className="workflow-timeline-content">
                          <div className="workflow-timeline-step">
                            {stepDef?.label || step.stepId}
                          </div>
                          <div className="workflow-timeline-meta">
                            <span style={{ color: ACTOR_COLORS[actor] }}>
                              {ACTOR_LABELS[actor]}
                            </span>
                            <span className="workflow-timeline-separator">•</span>
                            <span>{formatTimestamp(step.updatedAt)}</span>
                            <span className="workflow-timeline-separator">•</span>
                            <span className={`workflow-timeline-status workflow-timeline-status--${step.status}`}>
                              {step.status.replace('_', ' ')}
                            </span>
                          </div>
                          {step.note && (
                            <div className="workflow-timeline-note">{step.note}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                {currentSteps.every(step => !step.updatedAt && step.status === 'not_started') && (
                  <div className="workflow-timeline-empty">
                    <p className="ui-muted-text">No activity yet - all steps are pending</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
